import React, { useState, useEffect } from "react";
import { Search, Loader2, Save, Users, Settings, FileText, Lock, Unlock, AlertTriangle, CheckCircle2, ChevronDown, RefreshCw } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";
import SalarySlipModal from "../../components/admin/SalarySlipModal";
import { getCurrentPayPeriod, getRecentPayPeriodOptions } from "../../utils/salarySlipUtils";
import * as XLSX from "xlsx";

const PayrollConfigTab = ({ employees, loading, fetchEmployees, slipModalEmployee, setSlipModalEmployee }) => {
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(new Set());
    const EMPLOYEES_PER_PAGE = 10;

    const [payrollConfig, setPayrollConfig] = useState({
        useBasic: false, basicPercent: 50,
        useHRA: false, hraPercent: 50,
        useDA: false, daPercent: 10,
        usePF: false, pfPercent: 12, pfEmployerPercent: 12,
        useESI: false, esiPercent: 0.75, esiEmployerPercent: 3.25,
        useProfTax: false, profTaxAmount: 200,
        useOvertime: false, otHours: 0, otRate: 0,
        useBonus: false, bonusPercent: 8.33,
        useGratuity: false, gratuityYears: 5,
        useStayAllowance: false, stayAllowanceAmount: 0
    });

    const sortedEmployees = [...employees].sort((a, b) => {
        const idA = String(a.employeeId || "").toLowerCase();
        const idB = String(b.employeeId || "").toLowerCase();
        if (idA < idB) return -1;
        if (idA > idB) return 1;
        return 0;
    });

    const filteredEmployees = sortedEmployees.filter(emp => 
        (emp.name && emp.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (emp.employeeId && emp.employeeId.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const totalPages = Math.ceil(filteredEmployees.length / EMPLOYEES_PER_PAGE);
    const paginatedEmployees = filteredEmployees.slice(
        (currentPage - 1) * EMPLOYEES_PER_PAGE,
        currentPage * EMPLOYEES_PER_PAGE
    );

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedEmployeeIds(new Set(filteredEmployees.map(emp => emp._id)));
        } else {
            setSelectedEmployeeIds(new Set());
        }
    };

    const handleSelectEmployee = (id) => {
        const newSelected = new Set(selectedEmployeeIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedEmployeeIds(newSelected);
    };

    const handleSaveConfig = async () => {
        if (selectedEmployeeIds.size === 0) {
            toast.error("Please select at least one employee");
            return;
        }

        try {
            setSaving(true);
            await api.post("/api/admin/employees/payroll-config/bulk", {
                employeeIds: Array.from(selectedEmployeeIds),
                payrollConfig
            });
            toast.success("Payroll configuration applied successfully!");
            setSelectedEmployeeIds(new Set());
            fetchEmployees();
        } catch (err) {
            console.error(err);
            toast.error("Failed to apply payroll configuration");
        } finally {
            setSaving(false);
        }
    };

    const loadConfigFromEmployee = (employee) => {
        if (employee.payrollConfig) {
            setPayrollConfig({ ...payrollConfig, ...employee.payrollConfig });
            toast.success(`Loaded config from ${employee.name}`);
        } else {
            toast.error("This employee does not have a custom payroll config saved.");
        }
    };

    const getGroupLabel = (emp) => {
        if (!emp.payrollConfig || Object.keys(emp.payrollConfig).length === 0) {
            return "Unconfigured / Default";
        }
        const activeComponents = [];
        if (emp.payrollConfig.useBasic) activeComponents.push("Basic");
        if (emp.payrollConfig.useHRA) activeComponents.push("HRA");
        if (emp.payrollConfig.useDA) activeComponents.push("DA");
        if (emp.payrollConfig.usePF) activeComponents.push("PF");
        if (emp.payrollConfig.useESI) activeComponents.push("ESI");
        if (emp.payrollConfig.useProfTax) activeComponents.push("Prof. Tax");
        if (emp.payrollConfig.useOvertime) activeComponents.push("Overtime");
        if (emp.payrollConfig.useBonus) activeComponents.push("Bonus");
        if (emp.payrollConfig.useGratuity) activeComponents.push("Gratuity");
        if (emp.payrollConfig.useStayAllowance) activeComponents.push("Stay Allowance");

        if (activeComponents.length === 0) return "Custom (No active components)";
        return activeComponents.join(", ");
    };

    const groupedEmployees = paginatedEmployees.reduce((acc, emp) => {
        const label = getGroupLabel(emp);
        if (!acc[label]) acc[label] = [];
        acc[label].push(emp);
        return acc;
    }, {});
    
    const groupKeys = Object.keys(groupedEmployees).sort((a, b) => {
        if (a === "Unconfigured / Default") return 1;
        if (b === "Unconfigured / Default") return -1;
        return a.localeCompare(b);
    });

    const configFields = [
        { id: 'useBasic', label: 'Basic Salary', dep: 'basicPercent', depLabel: '% of Gross' },
        { id: 'useHRA', label: 'HRA', dep: 'hraPercent', depLabel: '% of Basic' },
        { id: 'useDA', label: 'DA', dep: 'daPercent', depLabel: '% of Basic' },
        { id: 'usePF', label: 'PF (Employee)', dep: 'pfPercent', depLabel: '% of Basic' },
        { id: 'useESI', label: 'ESI (Employee)', dep: 'esiPercent', depLabel: '% of Gross' },
        { id: 'useProfTax', label: 'Professional Tax', dep: 'profTaxAmount', depLabel: 'Amount (₹)' },
        { id: 'useOvertime', label: 'Overtime', dep: 'otHours', depLabel: 'OT Hours', dep2: 'otRate', dep2Label: 'OT Rate (₹)' },
        { id: 'useBonus', label: 'Bonus', dep: 'bonusPercent', depLabel: '% of Gross' },
        { id: 'useGratuity', label: 'Gratuity', dep: 'gratuityYears', depLabel: 'Years' },
        { id: 'useStayAllowance', label: 'Stay Allowance', dep: 'stayAllowanceAmount', depLabel: 'Amount (₹)' }
    ];

    return (
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row p-6 gap-6">
            <div className="w-full lg:w-1/3 flex flex-col bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl overflow-hidden shadow-sm">
                <div className="p-4 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] flex justify-between items-center">
                    <h2 className="font-bold text-[var(--text-primary)]">Salary Components</h2>
                    <button
                        onClick={handleSaveConfig}
                        disabled={saving || selectedEmployeeIds.size === 0}
                        className="flex items-center gap-2 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-all"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Apply ({selectedEmployeeIds.size})
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {configFields.map((cfg) => (
                        <div key={cfg.id} className="flex flex-col gap-2 p-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)]/50 dark:bg-neutral-800/20">
                            <label className="flex items-center gap-2 text-sm font-bold text-[var(--text-primary)] cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={payrollConfig[cfg.id]} 
                                    onChange={(e) => setPayrollConfig({ ...payrollConfig, [cfg.id]: e.target.checked })}
                                    className="w-4 h-4 text-[var(--accent-primary)] rounded border-gray-300 focus:ring-[var(--accent-primary)]"
                                />
                                {cfg.label}
                            </label>
                            {payrollConfig[cfg.id] && (
                                <div className="flex gap-3 mt-1 pl-6">
                                    <div className="flex flex-col flex-1">
                                        <span className="text-xs text-[var(--text-secondary)] mb-1">{cfg.depLabel}</span>
                                        <input 
                                            type="number" 
                                            value={payrollConfig[cfg.dep]} 
                                            onChange={(e) => setPayrollConfig({ ...payrollConfig, [cfg.dep]: e.target.value })}
                                            className="w-full text-sm border border-[var(--border-color)] rounded-lg px-3 py-1.5 dark:bg-neutral-800 dark:text-white"
                                        />
                                    </div>
                                    {cfg.dep2 && (
                                        <div className="flex flex-col flex-1">
                                            <span className="text-xs text-[var(--text-secondary)] mb-1">{cfg.dep2Label}</span>
                                            <input 
                                                type="number" 
                                                value={payrollConfig[cfg.dep2]} 
                                                onChange={(e) => setPayrollConfig({ ...payrollConfig, [cfg.dep2]: e.target.value })}
                                                className="w-full text-sm border border-[var(--border-color)] rounded-lg px-3 py-1.5 dark:bg-neutral-800 dark:text-white"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="w-full lg:w-2/3 flex flex-col bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl overflow-hidden shadow-sm">
                <div className="p-4 border-b border-[var(--border-color)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h2 className="font-bold text-[var(--text-primary)] flex items-center gap-2">
                        <Users size={18} />
                        Select Employees
                    </h2>
                    <div className="relative w-full sm:w-64">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 border border-[var(--border-color)] rounded-lg text-sm bg-[var(--bg-secondary)] dark:text-white focus:ring-2 focus:ring-[var(--accent-primary)] outline-none"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="h-full flex items-center justify-center">
                            <Loader2 size={32} className="animate-spin text-[var(--text-secondary)]" />
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-[var(--bg-secondary)] sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] w-12">
                                        <input 
                                            type="checkbox"
                                            checked={filteredEmployees.length > 0 && selectedEmployeeIds.size === filteredEmployees.length}
                                            onChange={handleSelectAll}
                                            className="w-4 h-4 rounded border-gray-300"
                                        />
                                    </th>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider w-[40%]">Employee</th>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider w-[20%]">Department</th>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider w-[20%]">Gross Salary</th>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider text-right w-[20%]">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                                {groupKeys.map(groupLabel => {
                                    const groupEmps = groupedEmployees[groupLabel];
                                    const allSelected = groupEmps.length > 0 && groupEmps.every(emp => selectedEmployeeIds.has(emp._id));
                                    
                                    return (
                                        <React.Fragment key={groupLabel}>
                                            <tr className="bg-gray-100 dark:bg-neutral-800/80">
                                                <td className="px-4 py-2 border-b border-[var(--border-color)]">
                                                    <input 
                                                        type="checkbox"
                                                        checked={allSelected}
                                                        onChange={(e) => {
                                                            const newSelected = new Set(selectedEmployeeIds);
                                                            if (e.target.checked) {
                                                                groupEmps.forEach(emp => newSelected.add(emp._id));
                                                            } else {
                                                                groupEmps.forEach(emp => newSelected.delete(emp._id));
                                                            }
                                                            setSelectedEmployeeIds(newSelected);
                                                        }}
                                                        className="w-4 h-4 rounded border-gray-300"
                                                    />
                                                </td>
                                                <td colSpan="4" className="px-4 py-2 border-b border-[var(--border-color)] text-[10px] font-bold text-[var(--text-primary)] uppercase tracking-wider">
                                                    {groupLabel} ({groupEmps.length} employee{groupEmps.length !== 1 ? 's' : ''})
                                                </td>
                                            </tr>
                                            {groupEmps.map(emp => (
                                                <tr 
                                                    key={emp._id} 
                                                    className={`hover:bg-[var(--bg-secondary)] transition-colors ${selectedEmployeeIds.has(emp._id) ? 'bg-[var(--accent-light)]/50 dark:bg-[var(--accent-subtle)]' : ''}`}
                                                >
                                                    <td className="px-4 py-3 w-12">
                                                        <input 
                                                            type="checkbox"
                                                            checked={selectedEmployeeIds.has(emp._id)}
                                                            onChange={() => handleSelectEmployee(emp._id)}
                                                            className="w-4 h-4 rounded border-gray-300"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="font-semibold text-sm text-[var(--text-primary)] flex items-center gap-2">
                                                            {emp.name}
                                                        </div>
                                                        <div className="text-xs text-[var(--text-secondary)]">{emp.employeeId}</div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                                                        {emp.team || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-[var(--text-primary)]">
                                                        ₹{emp.salary || 0}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <button
                                                            onClick={() => loadConfigFromEmployee(emp)}
                                                            className="text-xs font-semibold text-[var(--accent-primary)] hover:underline"
                                                            title="Load this employee's config into the editor"
                                                        >
                                                            Load Config
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                                {filteredEmployees.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-8 text-center text-[var(--text-secondary)]">
                                            No employees found matching your search.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                    {filteredEmployees.length > 0 && (
                        <div className="p-4 border-t border-[var(--border-color)] flex items-center justify-between bg-[var(--card-bg)] sticky bottom-0">
                            <div className="text-sm text-[var(--text-secondary)]">
                                Showing {(currentPage - 1) * EMPLOYEES_PER_PAGE + 1} to {Math.min(currentPage * EMPLOYEES_PER_PAGE, filteredEmployees.length)} of {filteredEmployees.length} employees
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1.5 border border-[var(--border-color)] rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--bg-secondary)]"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1.5 border border-[var(--border-color)] rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--bg-secondary)]"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const SalarySlipsTab = ({ setSlipModalEmployee, refreshConfigTab, companySettings }) => {
    const [periodKey, setPeriodKey] = useState(() => getCurrentPayPeriod(companySettings?.payCycle).key);

    useEffect(() => {
        if (companySettings) setPeriodKey(getCurrentPayPeriod(companySettings.payCycle).key);
    }, [companySettings]);
    const [statusData, setStatusData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [lockingState, setLockingState] = useState(null);

    const periodOptions = getRecentPayPeriodOptions(12, companySettings?.payCycle);

    const loadStatus = async (key) => {
        try {
            setLoading(true);
            const res = await api.get("/api/admin/salary-slips/period-status", {
                params: { periodKey: key },
                cache: false
            });
            setStatusData(res.data);
            return res.data;
        } catch (err) {
            toast.error("Failed to load slip statuses");
            console.error(err);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = () => {
        if (!statusData || !statusData.employees) {
            toast.error("No data available to export");
            return;
        }
        
        try {
            const exportData = statusData.employees.map(emp => ({
                "Employee Name": emp.name,
                "Employee ID": emp.employeeId || "N/A",
                "Department": emp.team || "N/A",
                "Working Days": emp.workDays ?? "-",
                "Paid Days": emp.paidDays ?? "-",
                "Leave Taken": emp.leaveTakenDays ?? "-",
                "Leave Dates": emp.leaveDates?.length ? emp.leaveDates.map(d => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })).join(', ') : "-",
                "Loss of Pay (LOP)": emp.lopDays ?? "-",
                "LOP/Absent Dates": emp.absentDates?.length ? emp.absentDates.map(d => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })).join(', ') : "-",
                "Monthly Salary (Base)": emp.originalGrossBase ? `₹${emp.originalGrossBase}` : "-",
                "Earned Gross Pay": emp.gross ? `₹${emp.gross}` : "-",
                "Net Pay": emp.net ? `₹${emp.net}` : "-",
                "Status": emp.status === "locked" ? "Finalized" : (emp.status === "draft" ? "Draft" : "Missing")
            }));
            
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Salary Slips");
            XLSX.writeFile(workbook, `Salary_Report_${periodKey}.xlsx`);
            toast.success("Excel exported successfully!");
        } catch (err) {
            console.error("Export Error:", err);
            toast.error("Failed to export Excel file");
        }
    };

    const autoGenerateSlips = async (key) => {
        const toastId = toast.loading("Auto-generating salary slips... this may take a few seconds.");
        try {
            setGenerating(true);
            const res = await api.post("/api/admin/salary-slips/auto-generate", { periodKey: key });
            toast.success(`Generated ${res.data.generated} salary slips.`, { id: toastId });
            await loadStatus(key);
            refreshConfigTab();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to auto-generate salary slips", { id: toastId });
        } finally {
            setGenerating(false);
        }
    };

    useEffect(() => {
        loadStatus(periodKey);
    }, [periodKey]);

    const handleLockToggle = async (slipId, currentLocked) => {
        if (!slipId) return;
        try {
            setLockingState(slipId);
            await api.patch(`/api/admin/salary-slips/${slipId}/lock`, { locked: !currentLocked });
            toast.success(currentLocked ? "Salary slip unlocked (draft mode)" : "Salary slip locked");
            loadStatus(periodKey);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update lock status");
        } finally {
            setLockingState(null);
        }
    };

    const handleViewEdit = (emp) => {
        setSlipModalEmployee(emp);
    };

    return (
        <div className="flex-1 flex flex-col p-6 overflow-hidden">
            <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl overflow-hidden shadow-sm flex-1 flex flex-col">
                <div className="p-4 border-b border-[var(--border-color)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <select
                                value={periodKey}
                                onChange={(e) => setPeriodKey(e.target.value)}
                                className="appearance-none bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] py-2 pl-4 pr-10 rounded-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
                            >
                                {periodOptions.map((opt, idx) => (
                                    <option key={`${opt.key || 'opt'}-${idx}`} value={opt.key}>{opt.label}</option>
                                ))}
                            </select>
                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] pointer-events-none" />
                        </div>
                        <button
                            onClick={() => autoGenerateSlips(periodKey)}
                            disabled={generating || statusData?.monthLocked}
                            className="bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
                        >
                            <RefreshCw size={14} className={generating ? "animate-spin" : ""} />
                            Regenerate Drafts
                        </button>
                        <button
                            onClick={handleExportExcel}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all"
                        >
                            <FileText size={14} />
                            Export Excel
                        </button>
                        {generating && <span className="text-sm text-[var(--accent-primary)] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Auto-generating slips...</span>}
                    </div>
                    {statusData && (
                        <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500"></span> Locked: {statusData.summary.locked}</div>
                            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500"></span> Draft: {statusData.summary.draft}</div>
                            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500"></span> Missing: {statusData.summary.missing}</div>
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-auto">
                    {loading && !generating ? (
                        <div className="h-full flex items-center justify-center">
                            <Loader2 size={32} className="animate-spin text-[var(--text-secondary)]" />
                        </div>
                    ) : statusData && statusData.employees ? (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-[var(--bg-secondary)] sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider w-1/3">Employee</th>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider w-1/6">Gross Pay</th>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider w-1/6">Net Pay</th>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider w-1/6">Status</th>
                                    <th className="px-4 py-3 border-b border-[var(--border-color)] text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider text-right w-1/6">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                                {statusData.employees.map((emp, index) => (
                                    <tr key={emp._id || index} className="hover:bg-[var(--bg-secondary)] transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-sm text-[var(--text-primary)]">{emp.name}</div>
                                            <div className="text-xs text-[var(--text-secondary)]">{emp.employeeId || 'No ID'} • {emp.team || 'No Team'}</div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                                            {emp.gross !== null ? `₹${emp.gross}` : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-[var(--text-primary)]">
                                            {emp.net !== null ? `₹${emp.net}` : '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                            {emp.status === 'locked' ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs font-bold">
                                                    <Lock size={12} /> Locked
                                                </span>
                                            ) : emp.status === 'draft' ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-xs font-bold">
                                                    <Unlock size={12} /> Draft
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-xs font-bold">
                                                    <AlertTriangle size={12} /> Missing
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-3">
                                                <button
                                                    onClick={() => handleViewEdit(emp)}
                                                    className="text-xs font-semibold flex items-center gap-1 text-[var(--accent-primary)] hover:underline"
                                                >
                                                    <FileText size={14} />
                                                    View/Edit
                                                </button>
                                                {emp.slipId && (
                                                    <button
                                                        onClick={() => handleLockToggle(emp.slipId, emp.locked)}
                                                        disabled={statusData.monthLocked || lockingState === emp.slipId}
                                                        className={`text-xs font-semibold flex items-center gap-1 ${
                                                            statusData.monthLocked 
                                                                ? 'text-[var(--text-secondary)] cursor-not-allowed' 
                                                                : emp.locked 
                                                                    ? 'text-amber-600 hover:text-amber-700' 
                                                                    : 'text-emerald-600 hover:text-emerald-700'
                                                        }`}
                                                    >
                                                        {lockingState === emp.slipId ? <Loader2 size={14} className="animate-spin" /> : emp.locked ? <Unlock size={14} /> : <Lock size={14} />}
                                                        {emp.locked ? "Unlock" : "Lock"}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const AdminPayroll = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [slipModalEmployee, setSlipModalEmployee] = useState(null);
    const [activeTab, setActiveTab] = useState('slips');
    const [companySettings, setCompanySettings] = useState(null);

    useEffect(() => {
        document.title = "Payroll Management | Savrion Technologies";
        fetchEmployees();
        api.get("/api/settings").then(res => setCompanySettings(res.data)).catch(console.error);
    }, []);

    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const res = await api.get("/api/admin/employees", {
                params: { page: 1, limit: 1000 }
            });
            setEmployees(Array.isArray(res.data.data) ? res.data.data : []);
        } catch (err) {
            toast.error("Failed to load employees");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen flex-col xl:flex-row bg-[var(--bg-primary)] text-[var(--text-primary)] font-sans">
            <AdminNavbar />
            <div className="flex-1 flex flex-col h-screen overflow-hidden">
                <header className="flex-shrink-0 bg-[var(--card-bg)] border-b border-[var(--border-color)] px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-xl font-bold text-[var(--text-primary)] flex items-center gap-2">
                            <FileText size={22} className="text-[var(--accent-primary)] dark:text-[var(--accent-primary)]" />
                            Payroll Management
                        </h1>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">
                            Auto-generate, review, and lock salary slips.
                        </p>
                    </div>
                    <div className="flex bg-gray-100 dark:bg-neutral-800 p-1 rounded-lg">
                        <button
                            onClick={() => setActiveTab('slips')}
                            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'slips' ? 'bg-[var(--card-bg)] dark:bg-neutral-700 text-[var(--accent-primary)] dark:text-white shadow-sm' : 'text-[var(--text-secondary)] hover:text-gray-900 dark:hover:text-white'}`}
                        >
                            Salary Slips
                        </button>
                        <button
                            onClick={() => setActiveTab('config')}
                            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'config' ? 'bg-[var(--card-bg)] dark:bg-neutral-700 text-[var(--accent-primary)] dark:text-white shadow-sm' : 'text-[var(--text-secondary)] hover:text-gray-900 dark:hover:text-white'}`}
                        >
                            Configuration
                        </button>
                    </div>
                </header>

                {activeTab === 'slips' ? (
                    <SalarySlipsTab 
                        setSlipModalEmployee={setSlipModalEmployee} 
                        refreshConfigTab={fetchEmployees}
                        companySettings={companySettings}
                    />
                ) : (
                    <PayrollConfigTab 
                        employees={employees} 
                        loading={loading} 
                        fetchEmployees={fetchEmployees}
                        setSlipModalEmployee={setSlipModalEmployee} 
                    />
                )}
            </div>

            <SalarySlipModal
                open={!!slipModalEmployee}
                onClose={() => setSlipModalEmployee(null)}
                employee={slipModalEmployee}
            />
        </div>
    );
};

export default AdminPayroll;

